<template lang="pug">
#tooltip.tooltip.absolute.normal-theme.md_max-w-prose.pointer-events-none.rounded-md.border-2.p-2.text-center.drop-shadow-md.w-max.bg-primary-100.text-primary-900.border-primary-200(
  :class="{ 'hidden inset-[-100vmax]': !tooltipText }"
  class="max-w-[80vw]"
  ref="tooltipRef"
  role="tooltip"
  v-if="!audioDoc && !wordDoc"
)
  uc-text(:k="tooltipKey" :text="tooltipText" v-if="tooltipText")
</template>

<script setup lang="ts">
import { inject, ref, unref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import useContent from '../stores/useContent';
import { storeToRefs } from 'pinia';
import gsap from 'gsap';
import { useEventListener } from '@vueuse/core';

const { compiled: content } = storeToRefs(useContent());
const tooltipRef = ref<HTMLElement | null>(null);
const activeLink = ref<HTMLAnchorElement | null>(null);
const tooltipKey = ref<String>();
const tooltipText = ref<String>();
const wordDoc = inject('wordDoc') as Ref<boolean>;
const audioDoc = inject('audioDoc');
let tooltipEl, animIn, animOut;

onMounted(() => {
  if (audioDoc || wordDoc.value) return;

  tooltipEl = unref(tooltipRef);
  animIn = gsap.timeline({ paused: true }).fromTo(
    tooltipEl,
    {
      scale: 0.9,
      opacity: 0
    },
    {
      scale: 1,
      opacity: 1,
      duration: 0.2,
      ease: 'easeOut'
    }
  );

  animOut = gsap.timeline({ paused: true }).fromTo(
    tooltipEl,
    {
      scale: 1,
      opacity: 1
    },
    {
      scale: 0.9,
      opacity: 0,
      duration: 0.1,
      ease: 'easeOut'
    }
  );

  tooltipEl.style.opacity = '0';
});

function setTooltip(link: HTMLAnchorElement) {
  link.setAttribute('aria-describedby', 'tooltip');
  link.classList.add('tooltiplink');
}

function isTooltip(e: Event) {
  const target = e.target as HTMLAnchorElement;
  if (target.getAttribute) {
    const href = target.getAttribute('href');
    return href && href.startsWith('#tooltip');
  }
}

function getTooltip(id: String) {
  const tooltips = content.value.tooltips;
  if (!tooltips) return null;
  const index = tooltips.findIndex(tooltip => tooltip.id === id);
  return index !== -1 ? tooltips[index] : null;
}

function getTooltipKey(link: HTMLAnchorElement) {
  const href = link.getAttribute('href') ?? '';
  const tooltipId = href.slice(1);
  return `${tooltipId}.text`;
}

function getTooltipText(link: HTMLAnchorElement) {
  const href = link.getAttribute('href') ?? '';
  const tooltipId = href.slice(1);
  const tooltip = getTooltip(tooltipId);

  if (tooltip) {
    return tooltip.text;
  } else {
    return `ERROR: finding tooltip: **${tooltipId}**`;
  }
}

async function showTooltip(link: HTMLAnchorElement) {
  if (link !== activeLink.value) hideTooltip();

  const screen = link.closest('.screen') as HTMLElement;

  activeLink.value = link;
  tooltipKey.value = getTooltipKey(activeLink.value) ?? '';
  tooltipText.value = getTooltipText(activeLink.value) ?? '';

  // set the title attr to get default tooltip behaviour in word docs
  if (wordDoc.value) {
    link.setAttribute('title', tooltipText.value as string);
  }

  if (tooltipEl && activeLink.value) {
    // allow the tooltip size to change once we set the reactive text
    await nextTick();

    // dark theme based on the closest screen
    if (screen && screen?.classList.contains('dark')) {
      tooltipEl?.classList.add('dark');
    } else {
      tooltipEl?.classList.remove('dark');
    }

    if (tooltipText.value.startsWith('ERROR:')) {
      tooltipEl?.classList.add('error');
    } else {
      tooltipEl?.classList?.remove('error');
    }

    positionTooltip(tooltipEl, activeLink.value);

    animOut.pause();
    animIn.play(0);
  }
}

function hideTooltip() {
  if (tooltipEl) {
    animIn.pause();
    animOut.play(0);
    tooltipText.value = '';
  }

  activeLink.value = null;
}

function positionTooltip(
  tooltipElement: HTMLElement,
  linkElement: HTMLElement
) {
  if (!linkElement) return;

  const tooltipRect = tooltipElement.getBoundingClientRect();
  const linkRect = linkElement.getBoundingClientRect();

  let leftPos: number, rightPos: number, topPos: number;

  // The link is wrapping if the start of the link is not the same as the left bounding rect edge. Then we place the tooltip at the start of the link.
  // +1 for small tolerance
  if (linkElement.offsetLeft > linkRect.left + 1) {
    leftPos = linkElement.offsetLeft;
  } else {
    const widthDiff = Math.abs(linkRect.width - tooltipRect.width);
    leftPos =
      linkRect.width >= tooltipRect.width
        ? linkRect.left + widthDiff / 2
        : linkRect.left - widthDiff / 2;
  }

  rightPos = leftPos + tooltipRect.width;
  topPos = linkRect.top - tooltipRect.height - 5;

  // default position is centered over the link
  tooltipElement.style.left = Math.round(leftPos) + 'px';
  tooltipElement.style.right = 'auto';
  tooltipElement.style.top = topPos + 'px';
  tooltipElement.style.bottom = 'auto';

  // keep tooltip horizontally within the viewport
  if (leftPos < 0) {
    tooltipElement.style.left = '5px';
  } else if (rightPos > window.innerWidth) {
    tooltipElement.style.left = 'auto';
    tooltipElement.style.right = '5px';
  }
}

function handleIn(e: Event) {
  if (isTooltip(e)) {
    const link = (e.target as HTMLElement).closest('a') as HTMLAnchorElement;
    setTooltip(link);
    if (link !== activeLink.value) showTooltip(link);
  }
}

function handleOut(e: Event) {
  if (isTooltip(e)) {
    if (activeLink.value) hideTooltip();
  }
}

function handleClick(e: Event) {
  if (isTooltip(e)) {
    e.preventDefault();
  }
}

// hide the tooltip when the section is scrolled
useEventListener(window, 'scrollsectionscrolled', evt => {
  if (activeLink.value) {
    hideTooltip();
  }
});

window.addEventListener('focusin', handleIn);
window.addEventListener('mouseover', handleIn);
window.addEventListener('focusout', handleOut);
window.addEventListener('mouseout', handleOut);
window.addEventListener('click', handleClick);

onBeforeUnmount(() => {
  window.removeEventListener('focusin', handleIn);
  window.removeEventListener('mouseover', handleIn);
  window.removeEventListener('focusout', handleOut);
  window.removeEventListener('mouseout', handleOut);
  window.removeEventListener('click', handleClick);
});
</script>

<style scoped>
a.tooltiplink {
  @apply cursor-help;
}

.dark {
  @apply bg-primary-800 text-primary-100 border-primary-600/50;
}

.error {
  @apply border-red-200 bg-red-100 text-red-900;
}
</style>
